import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

interface ImageUploadProps {
  value: string | null;
  onChange: (url: string | null) => void;
  formId: string;
  fieldId: string;
  label?: string;
  accept?: string;
  maxSizeMB?: number;
  placeholder?: string;
  className?: string;
  showUrlInput?: boolean;
}

export default function ImageUpload({
  value,
  onChange,
  formId,
  fieldId,
  label,
  accept = 'image/*',
  maxSizeMB = 10,
  placeholder = 'https://example.com/image.jpg',
  className = '',
  showUrlInput = true,
}: ImageUploadProps) {
  const { t } = useTranslation('common');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileUpload(file: File) {
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('imageUpload.errors.invalidFileType'));
      return;
    }

    // Validate file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(t('imageUpload.errors.fileTooLarge', { size: maxSizeMB }));
      return;
    }

    try {
      setUploading(true);

      // Step 1: Get presigned upload URL
      const presignResponse = await api.post('/api/upload/presign', {
        formId,
        fieldId,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        fieldType: 'image',
      });

      if (!presignResponse.ok) {
        const data = await presignResponse.json() as { error?: string };
        throw new Error(data.error || 'Failed to get upload URL');
      }

      const { uploadId, uploadUrl } = await presignResponse.json() as {
        uploadId: string;
        uploadUrl: string;
      };

      // Step 2: Upload the file
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      const uploadResult = await uploadResponse.json() as { url?: string };

      // Step 3: Get the public URL
      const publicUrl = uploadResult.url || `/api/files/${uploadId}`;
      onChange(publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('imageUpload.errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  function handleRemove() {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}

      {/* Preview / Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg transition-colors ${
          dragOver
            ? 'border-primary-500 bg-primary-50'
            : value
            ? 'border-gray-200'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {value ? (
          <div className="relative">
            <img
              src={value}
              alt="Uploaded preview"
              className="w-full h-32 object-cover rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text fill="%239ca3af" font-family="sans-serif" font-size="12" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">${t('imageUpload.invalidUrl')}</text></svg>`;
              }}
            />
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title={t('imageUpload.removeImage')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div
            className="p-6 text-center cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2"></div>
                <p className="text-sm text-gray-500">{t('imageUpload.uploading')}</p>
              </div>
            ) : (
              <>
                <svg
                  className="mx-auto h-10 w-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: t('imageUpload.dropOrClick') }}></p>
                <p className="mt-1 text-xs text-gray-400">{t('imageUpload.maxSize', { size: maxSizeMB })}</p>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* URL Input (optional) */}
      {showUrlInput && (
        <div className="mt-3">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span>{t('imageUpload.orEnterUrl')}</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            className="input text-sm"
            placeholder={placeholder}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
